import { useState } from "react";

import { Column, Row, Text } from "@/components";
import { DateTimePicker } from "@/components/form/DateTimePicker";
import { Button } from "@/components/ui/button";

import {
  MS_PER_MINUTE,
  SCREEN_RESCHEDULE_QUICK_DELAYS_MINUTES,
  SCREENING_PROMPT_ACTION_CLASSNAME,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";

import { ScreeningStepHeader } from "./ScreeningStepHeader";

export const RescheduleStep = ({
  dateTime,
  isUpdatePending,
  onBack,
  onDismiss,
  onReschedule,
}: {
  dateTime: string;
  isUpdatePending: boolean;
  onBack: () => void;
  onDismiss: () => void;
  onReschedule: (isoDateTime: string) => void;
}) => {
  const [value, setValue] = useState(() => dates.isoToDatetimeLocal(dateTime));

  const applyQuickDelay = (minutes: number) => {
    mixpanelUtils.trackButtonClick(
      `Reschedule screen +${minutes.toString()}m`,
      MIXPANEL_SCREENS.DASHBOARD,
      { minutes },
    );
    onReschedule(
      new Date(
        new Date(dateTime).getTime() + minutes * MS_PER_MINUTE,
      ).toISOString(),
    );
  };

  const submit = () => {
    const iso = dates.datetimeLocalToIso(value);
    if (!iso) return;
    onReschedule(iso);
  };

  return (
    <Column className="gap-0">
      <ScreeningStepHeader
        title="Reschedule screen"
        onBack={onBack}
        onDismiss={onDismiss}
      />
      <Column className="gap-2 px-3.5 pb-2.5 pt-2">
        <Column className="gap-1">
          <Text className="text-[10px] font-normal text-slate-400">
            Push back (was {dates.formatTime(dateTime)})
          </Text>
          <Row className="gap-1.5">
            {SCREEN_RESCHEDULE_QUICK_DELAYS_MINUTES.map((minutes) => (
              <Button
                key={minutes}
                variant="outlineSecondary"
                size="sm"
                disabled={isUpdatePending}
                className={SCREENING_PROMPT_ACTION_CLASSNAME}
                onClick={() => {
                  applyQuickDelay(minutes);
                }}
              >
                +{minutes}m
              </Button>
            ))}
          </Row>
        </Column>
        <Column className="gap-1">
          <Text className="text-[10px] font-normal text-slate-400">
            Or pick a time
          </Text>
          <DateTimePicker
            value={value}
            onChange={setValue}
            minuteStep={5}
            // Tag the portaled calendar/time popover so modals it floats over
            // (e.g. the candidate sheet) don't treat clicks in it as "outside".
            contentClassName="z-[70] screening-prompt-portal"
            className="h-8 rounded-full px-3 text-[11px]"
          />
        </Column>
        <Button
          size="sm"
          className={cn(SCREENING_PROMPT_ACTION_CLASSNAME, "w-full")}
          disabled={!value || isUpdatePending}
          onClick={submit}
        >
          Update time
        </Button>
      </Column>
    </Column>
  );
};
